import { defineStore } from "pinia";
import { useMessage } from "naive-ui";
import type {
  SelectedModels,
  DomainType,
  DbModelType,
  AppTypes,
  Versions,
  ModuleType,
  Domains,
  Domain,
  FormType,
  Directory,
  FilesType,
} from "@/type";
import { getAllDomain, getAppType, getModule, postTablesAll } from "@/api/api";
import { Ref } from "vue";
interface State {
  theme: "light" | "dark" | null;
  models: SelectedModels;
  groupId: string;
  artifactId: string;
  description: string;
  templateName: string;
  javaVersion: string;
  grailFrameworkVersion: string;
  basePackage: string;
  domain: DomainType | null;
  domainValue: string;
  dbModel: DbModelType | null;
  appTypes: AppTypes;
  versions: Versions;
  versionValue: string;
  modelsData: ModuleType[];
  domains: Domains;
  step: "basis" | "db" | "code" | "preview";
  dbType: string;
  dbUrl: string;
  dbUserName: string;
  dbPassword: string;
  tables: string[];
  allTables: { value: string; label: string }[];
  directory: Directory;
  files: FilesType;
  formRef: Ref | null;
  logoUrl: string;
}
export const useMainStore = defineStore("main", {
  state: () =>
    ({
      theme: "light",
      logoUrl: require('@/assets/logo_white.png'),
      models: {},
      groupId: "",
      artifactId: "grail-demo",
      description: "",
      templateName: "",
      javaVersion: "",
      grailFrameworkVersion: "",
      basePackage: "",
      domain: null,
      domainValue: "",
      dbModel: null,
      appTypes: [],
      versions: [],
      versionValue: "",
      modelsData: [],
      domains: [],
      step: "basis",
      dbPassword: "",
      dbType: "MySql",
      dbUrl: "",
      dbUserName: "",
      tables: [],
      allTables: [],
      directory: [],
      files: {},
      formRef: null,
    } as State),
  getters: {
    form() {
      const _form: FormType = {
        templateName: this.templateName,
        javaVersion: this.javaVersion,
        grailFrameworkVersion: this.grailFrameworkVersion,
        groupId: this.groupId,
        artifactId: this.artifactId,
        description: this.description,
        basePackage: this.basePackage,
        subModules: Object.keys(this.models)
          .map((key) => ({
            subModuleName: key,
            packaging: this.models[key].packaging?.join("") as string,
          }))
          .filter((item) => item.subModuleName !== "parent"),
        domain: this.domain as DomainType,
        dbModel:
          this.tables.length === 0
            ? null
            : {
                dbType: this.dbType,
                dbUrl: this.dbUrl,
                dbPassword: this.dbPassword,
                dbUserName: this.dbUserName,
                tables: this.tables,
              },
        moduleDependencyModels: Object.keys(this.models).map((key) => ({
          modulekey: key,
          dependencyModels: this.models[key].dependencyModels,
        })),
      };
      return _form;
    },
  },
  actions: {
    initDefaultValue() {
      const defaultAppType = this.appTypes[0];
      this.updateTemplateName(defaultAppType.appTypeValue);
    },
    updateDomainValue(value: string) {
      const domain = this.domains.filter((item) => item.value === value)[0];
      this.domainValue = value;
      this.domain = {
        code: domain.code,
        parentCode: domain.parentCode,
        name: domain.name,
        desc: domain.domainDesc,
      };
    },
    updateVersionValue(value: string) {
      this.versionValue = value;
      const version = this.versions.filter(
        (item) => item.grailFrameworkVersion === value
      )[0];
      this.grailFrameworkVersion = value;
      this.javaVersion = version.javaVersion;
    },
    updateTemplateName(templateName: string) {
      this.templateName = templateName;
      this.getProjectModule();
    },
    updateModel(value: SelectedModels) {
      this.models = value;
    },
    updateGroupId(value: string) {
      console.log(value);
      this.groupId = value;
      if (value === "") {
        this.basePackage = "";
        return;
      }
      this.basePackage = this.groupId + "." + this.artifactId.replace("-", ".");
    },
    updateArtifactId(value: string) {
      this.artifactId = value;
      this.basePackage = `${this.groupId}.${this.artifactId.replace("-", ".")}`;
    },
    updateDirectory(value: Directory) {
      this.directory = value;
    },
    updateFiles(files: FilesType) {
      this.files = files;
    },
    toggleTheme(theme: "light" | "dark" | null) {  
      this.theme = theme;  
      if (this.theme === "light") { 
        //document.body.style.maxWidth="1680px";
        //document.body.style.margin=" 0 auto";
        document.body.style.backgroundColor= "#fff";
        this.logoUrl = require('@/assets/logo_white.png'); 
      } else if (this.theme === "dark") { 
        //document.body.style.maxWidth="1680px";
        //document.body.style.margin="0 auto";
        document.body.style.backgroundColor="#1b1f23";
        this.logoUrl = require('@/assets/logo_black.png'); 
      }  
    },
    async getAppType() {
      const res = await getAppType();
      this.appTypes = res.data;
      this.initDefaultValue();
    },
    async getProjectModule() {
      const res = await getModule(this.templateName);
      this.versions = res.data.grailVersions;
      const _modulesData = {
        ...res.data.parentModule,
        subModules: res.data.subModules,
        name: "parent",
      };
      this.versionValue = "";
      this.models = {};
      this.modelsData = [_modulesData];
    },
    async getAllDomain() {
      const res = await getAllDomain();
      this.domains = res.data.map((item: Domain) => ({
        ...item,
        value: item.code,
        label: item.name,
      }));
    },
    async getTables() {
      const message = useMessage();
      const res = await postTablesAll({
        dbType: this.dbType,
        dbUrl: this.dbUrl,
        dbUserName: this.dbUserName,
        dbPassword: this.dbPassword,
      });
      // @ts-ignore
      if (res.msgCode !== "200") {
        // @ts-ignore
        message.error(res.msgContent);
        return;
      }
      this.allTables = res.data.tables.map((v: string) => ({
        value: v,
        label: v,
      }));
    },
  },
});
